import React from "react";
import DOMPurify from "dompurify";

/**
 * Strict markdown renderer for tech-data bodies.
 *
 * Renders a constrained subset: headings, paragraphs, bold/italic, code,
 * unordered + ordered lists, simple pipe tables, blockquotes. NO raw HTML,
 * NO links to javascript:, NO images, NO scripts.
 *
 * We deliberately implement this with a tiny in-file parser instead of
 * react-markdown so we don't add another dependency and so the allowlist is
 * trivial to audit.
 */
function escapeHTML(s) {
    return String(s)
        .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

function renderInline(s) {
    let out = escapeHTML(s);
    // bold **text**
    out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
    // italic *text* (avoid matching inside bold)
    out = out.replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, "<em>$1</em>");
    // inline code `text`
    out = out.replace(/`([^`]+)`/g, '<code class="px-1 rounded bg-neutral-200/70 dark:bg-neutral-800 text-[0.85em]">$1</code>');
    return out;
}

function renderBlocks(md) {
    const lines = String(md || "").replace(/\r\n/g, "\n").split("\n");
    const out = [];
    let i = 0;
    while (i < lines.length) {
        const line = lines[i];

        // Headings
        const h = line.match(/^(#{1,4})\s+(.*)$/);
        if (h) {
            const level = h[1].length + 1;            // h2..h5 (no h1 — page owns h1)
            out.push(`<h${level} class="text-base sm:text-lg font-semibold mt-4">${renderInline(h[2])}</h${level}>`);
            i++;
            continue;
        }

        // Pipe table — minimum 2 rows where row 2 is separator
        if (line.includes("|") && lines[i + 1] && /^\s*\|?\s*[-:]+/.test(lines[i + 1])) {
            const header = line.split("|").map((c) => c.trim()).filter(Boolean);
            i += 2;
            const rows = [];
            while (i < lines.length && lines[i].includes("|")) {
                rows.push(lines[i].split("|").map((c) => c.trim()).filter((_c, idx, a) => a.length > 0).filter(Boolean));
                i++;
            }
            const thead = "<thead><tr>" + header.map((c) => `<th class="text-left text-xs uppercase tracking-wider opacity-70 py-1 pr-4">${renderInline(c)}</th>`).join("") + "</tr></thead>";
            const tbody = "<tbody>" + rows.map((r) =>
                "<tr>" + r.map((c) => `<td class="py-1 pr-4 text-sm">${renderInline(c)}</td>`).join("") + "</tr>"
            ).join("") + "</tbody>";
            out.push(`<table class="w-full border-collapse">${thead}${tbody}</table>`);
            continue;
        }

        // Ordered list
        if (/^\s*\d+\.\s+/.test(line)) {
            const items = [];
            while (i < lines.length && /^\s*\d+\.\s+/.test(lines[i])) {
                items.push(lines[i].replace(/^\s*\d+\.\s+/, ""));
                i++;
            }
            out.push("<ol class=\"list-decimal pl-6 space-y-1 my-2\">" + items.map((t) => `<li>${renderInline(t)}</li>`).join("") + "</ol>");
            continue;
        }

        // Unordered list
        if (/^\s*[-*]\s+/.test(line)) {
            const items = [];
            while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
                items.push(lines[i].replace(/^\s*[-*]\s+/, ""));
                i++;
            }
            out.push("<ul class=\"list-disc pl-6 space-y-1 my-2\">" + items.map((t) => `<li>${renderInline(t)}</li>`).join("") + "</ul>");
            continue;
        }

        if (line.trim() === "") { i++; continue; }

        out.push(`<p class="leading-relaxed text-sm my-2">${renderInline(line)}</p>`);
        i++;
    }
    return out.join("\n");
}

export function TechDataMarkdownView({ body, className = "" }) {
    // Defense-in-depth: the renderer already escapes all user input and only
    // emits an allow-listed subset of tags, but we run the output through
    // DOMPurify (strict allow-list) as a final, trusted sanitization pass.
    const html = DOMPurify.sanitize(renderBlocks(body || ""), {
        ALLOWED_TAGS: [
            "h2", "h3", "h4", "h5", "p", "strong", "em", "code", "ul", "ol",
            "li", "table", "thead", "tbody", "tr", "th", "td", "blockquote", "br",
        ],
        ALLOWED_ATTR: ["class"],
    });
    return (
        <div
            data-testid="techdata-markdown"
            className={`prose-sm max-w-none text-neutral-800 dark:text-neutral-200 ${className}`}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
}
